import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { signInWithGoogleAndProcess } from '../../utils/googleSignIn';

/**
 * Compact signup block for modal contexts (guest exam gate). Google popup
 * sign-in happens in place, no navigation, so the surrounding flow (e.g. a
 * running exam) survives. Email & other methods go through the full auth page
 * via onMoreOptions.
 */
const GuestSignupCard = ({ onSuccess, onMoreOptions }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogle = async () => {
    setError('');
    setIsLoading(true);
    try {
      const { user } = await signInWithGoogleAndProcess();
      onSuccess?.(user);
    } catch (err) {
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup, not an error
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setError(t('auth.error.googleAccountExists'));
      } else if (err?.code === 'auth/popup-blocked') {
        // Popup blocked, the full auth page uses the redirect-friendly flow
        onMoreOptions?.();
        return;
      } else {
        setError(t('auth.error.googleFailed', { message: err?.message || '' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {error && (
        <p className="text-sm text-red-600" role="alert">{error}</p>
      )}
      <button
        type="button"
        onClick={handleGoogle}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-slate-300 bg-white text-slate-800 font-semibold shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:shadow-md focus:ring-2 focus:ring-slate-300 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer min-h-[52px] touch-manipulation text-base"
      >
        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>{isLoading ? t('tryExam.auth.signingIn', { defaultValue: 'Signing in…' }) : t('auth.variantB.googleButton', { defaultValue: 'Continue with Google' })}</span>
      </button>
      {onMoreOptions && (
        <button
          type="button"
          onClick={onMoreOptions}
          className="w-full min-h-[44px] inline-flex items-center justify-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors duration-200 cursor-pointer touch-manipulation"
        >
          {t('tryExam.auth.moreOptions', { defaultValue: 'Sign up with e-mail instead' })}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

export default GuestSignupCard;
